/**
 * TopicalGuidePage — editorial long-form guides migrated from the blog
 * into the Guides section. Different content type from
 * RegionalItineraryPage (which renders programmatic-SEO itinerary
 * collections).
 *
 * URL pattern: /guides/:region/:slug
 *   e.g. /guides/napa/castello-di-amorosa
 *        /guides/napa/uber-transportation
 *
 * Data: GET /api/guides/by-slug/:region/:slug (database, edited in
 * Admin > Guides); the shipped /topical-guides.json is the fallback when
 * the API is unreachable (lib/guidesSource).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Clock, Tag } from '@phosphor-icons/react';
import FAQSchema from '@/components/seo/FAQSchema';
import useJsonLd from '@/hooks/useJsonLd';
import useAdSettings from '@/hooks/useAdSettings';
import InArticleAd from '@/components/ads/InArticleAd';
import MultiplexAd from '@/components/ads/MultiplexAd';
import splitHtmlForAds, { endsWithCta } from '@/lib/splitHtmlForAds';
import { loadTopicalGuide } from '@/lib/guidesSource';

const SITE_URL = 'https://www.wineryfinderapp.com';
// Empty top-level marker between body segments; an in-article ad is
// portalled into each one. Zero height until the ad fills, so margins
// collapse through it and the prose spacing is unchanged.
const AD_ANCHOR = '<div class="not-prose" data-ad-anchor=""></div>';

const TopicalGuidePage = () => {
  const { region, slug } = useParams();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(null); // 'api' | 'fallback' (data-guide-source)

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadTopicalGuide(region, slug, { onSource: (s) => { if (alive) setSource(s); } })
      .then((guide) => { if (alive) setConfig(guide); })
      .catch(() => { if (alive) setConfig(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [region, slug]);

  // Pre-compute JSON-LD payloads BEFORE early returns (rules of hooks).
  const title = config?.title || '';
  const description = config?.seoDescription || '';
  // Canonical is computed from route params immediately — never wait for data load.
  // This prevents an empty canonical during the loading phase confusing Googlebot.
  const canonicalUrl = `${SITE_URL}/guides/${region}/${slug}`;
  const url = config ? canonicalUrl : canonicalUrl; // always the same; keep alias for compat
  const heroImage = config?.heroImage;

  const breadcrumbJson = config ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Winery Finder', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Wine Country Guides', item: `${SITE_URL}/wine-country-guides` },
      { '@type': 'ListItem', position: 3, name: config.region, item: `${SITE_URL}/wineries/${config.regionSlug}` },
      { '@type': 'ListItem', position: 4, name: config.title, item: url },
    ],
  } : null;

  const articleJson = config ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: heroImage,
    datePublished: config.lastUpdated || '2026-07-22',
    dateModified: config.lastUpdated || '2026-07-22',
    author: { '@type': 'Organization', name: 'Winery Finder' },
    publisher: {
      '@type': 'Organization',
      name: 'Winery Finder',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo192.png` },
    },
    mainEntityOfPage: url,
    articleSection: config.category,
  } : null;

  useJsonLd('topical-breadcrumb', breadcrumbJson);
  useJsonLd('topical-article', articleJson);

  // Body HTML, with up to 2 ad anchors at safe top-level boundaries (see
  // lib/splitHtmlForAds). Unchanged when ads are off.
  const { enabled: adsOn } = useAdSettings();
  const bodyHtml = useMemo(() => {
    const content = config?.content || '';
    return adsOn ? splitHtmlForAds(content).join(AD_ANCHOR) : content;
  }, [config, adsOn]);
  // Where the end-of-article Multiplex unit goes (see the JSX below).
  const hasFaqs = Array.isArray(config?.faqs) && config.faqs.length > 0;
  const bodyEndsWithCta = useMemo(() => endsWithCta(config?.content), [config]);
  const bodyRef = useRef(null);
  const [adAnchors, setAdAnchors] = useState([]);
  useLayoutEffect(() => {
    setAdAnchors(bodyRef.current ? Array.from(bodyRef.current.querySelectorAll('[data-ad-anchor]')) : []);
  }, [bodyHtml]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-24 px-6 text-center" data-testid="topical-guide-loading">
        <Helmet>
          <title>Loading… | Winery Finder</title>
          <link rel="canonical" href={`${SITE_URL}/guides/${region}/${slug}`} />
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="animate-pulse text-gray-500">Loading guide…</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="max-w-2xl mx-auto py-24 px-6 text-center" data-testid="topical-guide-not-found">
        <Helmet>
          <title>Guide not found | Winery Finder</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <h1 className="text-3xl font-bold">Not found</h1>
        <p className="mt-4 text-gray-600">This guide doesn&apos;t exist yet.</p>
        <Link to="/wine-country-guides" className="mt-6 inline-block text-wine-600 underline">
          Browse all guides →
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-16 md:py-20 px-6" data-testid="topical-guide-page" data-guide-source={source || undefined}>
      <Helmet>
        <title>{`${config.seoTitle || config.title} | Winery Finder`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={config.seoTitle || config.title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        {heroImage && <meta property="og:image" content={heroImage} />}
      </Helmet>
      <FAQSchema faqs={config.faqs || []} />

      <nav className="text-xs text-gray-500 mb-8" aria-label="Breadcrumb" data-testid="topical-guide-breadcrumbs">
        <Link to="/" className="hover:text-wine-600">Home</Link>
        <span className="mx-2">›</span>
        <Link to="/wine-country-guides" className="hover:text-wine-600">Wine Country Guides</Link>
        <span className="mx-2">›</span>
        <span className="text-gray-700">{config.region}</span>
        <span className="mx-2">›</span>
        <span className="text-gray-900 font-medium truncate inline-block max-w-xs align-bottom">{config.category}</span>
      </nav>

      <motion.header
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        data-testid="topical-guide-header"
      >
        <div className="flex flex-wrap items-center gap-3 text-xs mb-4">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-wine-50 text-wine-700 font-semibold uppercase tracking-widest"
            data-testid="topical-guide-category"
          >
            <Tag size={12} weight="fill" />
            {config.category}
          </span>
          <span className="text-gray-400">·</span>
          <span className="text-gray-600">{config.region}</span>
          {config.readTime && (
            <>
              <span className="text-gray-400">·</span>
              <span className="inline-flex items-center gap-1 text-gray-600" data-testid="topical-guide-read-time">
                <Clock size={12} />
                {config.readTime}
              </span>
            </>
          )}
        </div>
        <h1 className="text-3xl md:text-5xl font-serif tracking-tight text-gray-900">
          {config.title}
        </h1>
        {config.lastUpdated && (
          <p className="mt-3 text-xs text-gray-500" data-testid="topical-guide-last-updated">
            Updated{' '}
            {new Date(config.lastUpdated).toLocaleDateString('en-US', {
              month: 'long', day: 'numeric', year: 'numeric',
            })}
          </p>
        )}
      </motion.header>

      {heroImage && (
        <div
          className="mt-10 overflow-hidden rounded-2xl shadow-sm"
          data-testid="topical-guide-hero-image"
        >
          <img
            src={heroImage}
            alt={config.heroImageAlt || config.title}
            loading="lazy"
            className="w-full h-64 md:h-96 object-cover"
          />
        </div>
      )}

      {config.tldr && (
        <section
          className="mt-12 rounded-2xl border border-wine-100 bg-wine-50/40 p-6 md:p-8"
          data-testid="topical-guide-tldr"
        >
          <div className="text-xs font-bold uppercase tracking-widest text-wine-700 mb-3">
            TL;DR
          </div>
          <p className="text-base md:text-lg text-gray-800 leading-relaxed">
            {config.tldr}
          </p>
        </section>
      )}

      {/* Body — HTML rendered from the migrated blog post. Editorial
          content only, so trusting the source is acceptable here. */}
      <article
        ref={bodyRef}
        className="topical-guide-body mt-12 prose prose-lg max-w-none prose-headings:font-serif prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-wine-700 prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-img:shadow-sm prose-strong:text-gray-900 prose-li:text-gray-700"
        data-testid="topical-guide-body"
        dangerouslySetInnerHTML={{ __html: bodyHtml }}
      />
      {adAnchors.map((el, i) => createPortal(<InArticleAd position={`guide-${i + 1}`} />, el, `ad-${i}`))}

      {/* One Multiplex unit after the body, before the FAQ (or before the
          footer, whose first item is the pricing note, not a link/CTA). A
          body ending in a CTA ("Download the app: CLICK HERE") pushes it
          below the FAQ instead; with no FAQ to separate them it is left out. */}
      {!bodyEndsWithCta && <MultiplexAd />}

      {hasFaqs && (
        <section className="mt-20" data-testid="topical-guide-faqs">
          <h2 className="text-2xl font-serif text-gray-900 mb-6">Frequently asked</h2>
          <div className="space-y-6">
            {config.faqs.map((f, i) => (
              <div key={i} data-testid={`topical-guide-faq-${i}`}>
                <h3 className="text-lg font-medium text-gray-900">{f.q}</h3>
                <p className="mt-2 text-gray-700 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      {bodyEndsWithCta && hasFaqs && <MultiplexAd />}

      <section className="mt-20 pt-10 border-t border-gray-200" data-testid="topical-guide-footer-cta">
        <div
          className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900"
          data-testid="topical-guide-pricing-disclaimer"
        >
          <span className="mt-0.5 text-amber-700 flex-shrink-0" aria-hidden>ⓘ</span>
          <span>
            Any tasting fees, menus, or hours mentioned above were accurate as of{' '}
            {config.lastUpdated
              ? new Date(config.lastUpdated).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
              : 'July 2026'}
            . Prices and policies change frequently — call each winery to confirm
            before you book. Search{' '}
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`wineries in ${config.region}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold hover:text-amber-950"
            >
              {config.region} wineries on Google Maps
            </a>{' '}
            for the current phone, hours, and reservation policy.
          </span>
        </div>
        <p className="text-sm text-gray-500">
          Ready to build a trip? Try{' '}
          <Link to="/concierge" className="text-wine-600 underline">
            the AI concierge
          </Link>{' '}
          — it plans a 3-stop day in ~6 seconds. Or{' '}
          <Link to="/wine-country-guides" className="text-wine-600 underline">
            browse all regional guides
          </Link>.
        </p>
      </section>
    </div>
  );
};

export default TopicalGuidePage;
